/**
 * diffUpdatePayload — #36 (2026-06-11).
 *
 * Build an UPDATE payload containing ONLY the fields that changed versus
 * the pre-save baseline. Why this exists (B1 smoke finding):
 *
 *  1. Misleading op labels: update payloads that always carried the
 *     parent FK (agreement_id) made derive_op_label read every routine
 *     edit as "Attach to Agreement" on the pending badge.
 *  2. Noisy CR diffs: the Compliance reviewer saw unchanged fields in
 *     every diff — §3j information quality.
 *  3. Clears that never persisted (C-120 family): fields cleared to ''
 *     were OMITTED from the payload, so the clear silently no-opped.
 *     Changed-to-empty fields listed in `nullableOnClear` now send an
 *     explicit null, which the backend Update schemas accept and apply.
 *
 * Semantics per key of `values`:
 *   - key in `exclude`                    → omitted (FKs owned by
 *     dedicated attach/detach/move flows — never part of a field edit)
 *   - value strictly equal to `initial`   → omitted (unchanged)
 *   - changed to '' / null / undefined    → null if in `nullableOnClear`,
 *     otherwise omitted (required fields are blocked by validators
 *     before submit; non-clearable optionals keep today's no-op
 *     behaviour rather than inventing a clear the schema may reject)
 *   - changed to a real value             → included, after the per-key
 *     `transform` (e.g. parseInt coercions, Yes/No → boolean)
 *
 * CREATE payloads are intentionally NOT built with this helper — a new
 * record sends its full field set.
 */

export interface DiffPayloadOpts {
  /** Keys whose changed-to-empty state sends an explicit null. */
  nullableOnClear?: ReadonlySet<string>;
  /** Per-key transforms applied to CHANGED non-empty values. Return
   *  `undefined` to drop the key (e.g. failed numeric parse). */
  transform?: Record<string, (v: unknown) => unknown>;
  /** Keys never included regardless of change state. */
  exclude?: ReadonlySet<string>;
}

/** Stable JSON (object keys sorted) — so two equal objects built in a different
 *  key order compare equal. */
function stableJson(v: unknown): string {
  return JSON.stringify(v, (_k, val) => (
    val && typeof val === 'object' && !Array.isArray(val)
      ? Object.fromEntries(Object.keys(val as object).sort().map((key) => [key, (val as Record<string, unknown>)[key]]))
      : val));
}

/** Equal by value: primitives by ===, objects / arrays by content (P16b: the client-
 *  record blocks are objects, rebuilt by the editors, so === would always say
 *  "changed" and re-file the whole block with every Save). */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a && b && typeof a === 'object' && typeof b === 'object') return stableJson(a) === stableJson(b);
  return false;
}

export function diffUpdatePayload<T extends Record<string, unknown>>(
  values: T,
  initial: T,
  opts: DiffPayloadOpts = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(values)) {
    if (opts.exclude?.has(k)) continue;
    const v = values[k];
    if (sameValue(v, initial[k])) continue; // unchanged → omit
    const isEmpty = v === '' || v === null || v === undefined;
    if (isEmpty) {
      if (opts.nullableOnClear?.has(k)) out[k] = null;
      continue;
    }
    const t = opts.transform?.[k];
    if (t) {
      const tv = t(v);
      if (tv !== undefined) out[k] = tv;
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Shared transform: string → int, dropped when not parseable. */
export function toInt(v: unknown): unknown {
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) ? undefined : n;
}
